import { getServiceIdentity } from "@/serviceIdentity";
import type { ServiceEnvironment } from "@/shared-utils/serviceIdentity";
import { deploymentRegionSchema } from "@/utils/deployment";
import type { DeploymentRegion } from "@/utils/deployment";
import { getSiteOrigin, isNonProdListenLabsUrl } from "@/utils/helpers";

/**
 * Runtime platform + intent-named environment helpers.
 *
 * Use the shared identity for telemetry labels and isProductionEnvironment for
 * alert destinations. Use platform helpers for product side-effect gates:
 * `VERCEL_ENV`, `JOBS_ENV`, and `NODE_ENV` mean different things.
 *
 * Deployment region (US vs EU stack) is process-scoped: read DEPLOYMENT_REGION
 * / NEXT_PUBLIC_DEPLOYMENT_REGION. Validated on boot by Zod env modules.
 */

export type TelemetryEnv = ServiceEnvironment;

let _platformCache: "render" | "vercel" | "other" | null = null;
let _deploymentRegionCache: DeploymentRegion | null = null;

/**
 * The runtime platform: a Vercel deployment, the Render worker, or anything
 * else (local node, CI, etc.).
 */
export function detectPlatform(): "render" | "vercel" | "other" {
  if (_platformCache) {
    return _platformCache;
  }

  // Vercel automatically sets VERCEL_ENV
  if (process.env.VERCEL_ENV) {
    _platformCache = "vercel";
    return "vercel";
  }

  // Render automatically sets RENDER or RENDER_SERVICE_NAME
  if (process.env.RENDER || process.env.RENDER_SERVICE_NAME) {
    _platformCache = "render";
    return "render";
  }

  // Default to 'other' for non-Vercel/Render environments (like node locally)
  _platformCache = "other";
  return "other";
}

/**
 * Telemetry / analytics environment label across Vercel, Render, and local.
 * For telemetry labels. Use isProductionEnvironment for alert destinations.
 * Resolution order is in
 * `serviceIdentity.ts` (`OTEL_RESOURCE_ATTRIBUTES` first, platform fallbacks after).
 */
export function getTelemetryEnv(): TelemetryEnv {
  return getServiceIdentity().environment;
}

/** Staging deployment configuration overrides production telemetry fallbacks. */
export function isProductionEnvironment(): boolean {
  const isStaging = isJobsStaging() || isNonProdListenLabsUrl(getSiteOrigin());
  return !isStaging && getTelemetryEnv() === "production";
}

/** True when running under Jest / NODE_ENV=test. */
export function isTestRuntime(): boolean {
  return process.env.NODE_ENV === "test";
}

/** Local `next dev` / Node in development mode. */
export function isNodeDevelopment(): boolean {
  return process.env.NODE_ENV === "development";
}

/** Temporal / jobs worker identity, if this process is a worker. */
export function getWorkerEnv(): "temporal" | "jobs" | undefined {
  const workerEnv = process.env.WORKER_ENV;
  if (workerEnv === "temporal" || workerEnv === "jobs") {
    return workerEnv;
  }
  return undefined;
}

/** Platform production target, including staging Vercel; use isProductionEnvironment for alert destinations. */
export function isCustomerProduction(): boolean {
  return isJobsProduction() || isVercelProduction();
}

/** Prod jobs worker (`JOBS_ENV=production`). Not influenced by `VERCEL_ENV`. */
export function isJobsProduction(): boolean {
  return process.env.JOBS_ENV === "production";
}

/** Staging jobs worker (`JOBS_ENV=staging`). Deployed, but not production. */
export function isJobsStaging(): boolean {
  return process.env.JOBS_ENV === "staging";
}

/** Raw `JOBS_ENV`, for error messages that need to name what was configured. */
export function getJobsEnv(): string {
  return process.env.JOBS_ENV ?? "unset";
}

export function isVercelProduction(): boolean {
  return process.env.VERCEL_ENV === "production";
}

export function isVercelPreview(): boolean {
  return process.env.VERCEL_ENV === "preview";
}

/** Vercel deploy stage `development` (e.g. `vercel dev`). */
export function isVercelDevelopment(): boolean {
  return process.env.VERCEL_ENV === "development";
}

/** Deployed Vercel target (production or preview). */
export function isVercelDeployed(): boolean {
  return isVercelProduction() || isVercelPreview();
}

/**
 * Vercel stage for Next-only o11y (Sentry). Unset locally → `"development"`,
 * matching historical `VERCEL_ENV ?? "development"`.
 */
export function getVercelEnvOrDevelopment(): string {
  return process.env.VERCEL_ENV ?? "development";
}

/**
 * Which Listen Labs stack this process belongs to (parallel US / EU deploys).
 * Defaults to `missing` when unset so o11y shows a gap instead of a false US tag.
 * Prefer NEXT_PUBLIC_* on the client.
 */
export function getDeploymentRegion(): DeploymentRegion {
  if (_deploymentRegionCache) {
    return _deploymentRegionCache;
  }
  // Deployment config only. Telemetry attributes never reach routing or data gates.
  _deploymentRegionCache = deploymentRegionSchema.parse(
    process.env.DEPLOYMENT_REGION ||
      process.env.NEXT_PUBLIC_DEPLOYMENT_REGION ||
      "missing"
  );
  return _deploymentRegionCache;
}

/** Datadog / statsd tag fragment, e.g. `deployment_region:eu`. */
export function getDeploymentRegionTag(): string {
  return `deployment_region:${getDeploymentRegion()}`;
}
